import {
  CheckCircleOutlined,
  EditOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Form, Input, message, Modal, Select, Tag } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import SummaryLayout from '../../components/layout/SummaryLayout';
import { getData, postData } from '../../config/api-connect';
import { getColumnFilters, getColumnSearchProps } from '../../utils/filters';
import './gstSummary.css';

const { Option } = Select;

function VenueConfig() {
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState(() => {
    const saved = localStorage.getItem('selectedCity');
    return saved ? JSON.parse(saved) : '';
  });
  const [venueData, setVenueData] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    getData('/finance/get_cities_and_venues')
      .then((response) => {
        setCities(response.cities || []);
      })
      .catch((error) => {
        console.error('Error fetching cities:', error.message);
      });
  }, []);

  const getVenueConfigs = useCallback(() => {
    if (!selectedCityId) {
      setVenueData([]);
      return;
    }
    setLoading(true);

    getData(`/finance/get_venue_configs/?city_id=${selectedCityId}`)
      .then((response) => {
        setVenueData(response.result || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error('Error fetching venue configurations:', error.message);
      });
  }, [selectedCityId]);

  useEffect(() => {
    getVenueConfigs();
  }, [getVenueConfigs, selectedCityId]);

  const openEditModal = (record) => {
    setSelectedVenue(record);

    form.setFieldsValue({
      invoice_code: record.invoice_code,
      gstin: record.gstin,
      address: record.address,
      operating_model: record.operating_model_value,
    });

    setEditModalOpen(true);
  };

  const chooseCity = (value) => {
    setSelectedCityId(value);
    localStorage.setItem('selectedCity', JSON.stringify(value));
  };

  const saveVenueConfig = () => {
    form.validateFields().then((values) => {
      postData('/finance/edit_venue_config/', {
        venue_id: selectedVenue.id,
        ...values,
      })
        .then(() => {
          message.success('Venue config updated');
          setEditModalOpen(false);
          getVenueConfigs();
          form.resetFields();
          setSelectedVenue(null);
        })
        .catch(() => {
          message.error('Failed to update venue config');
        });
    });
  };

  const uniqueValues = (key) =>
    Array.from(new Set(venueData.map((r) => r[key]).filter(Boolean))).sort();

  const columns = [
    {
      title: 'Venue',
      dataIndex: 'name',
      align: 'left',
      width: 250,
      ...getColumnSearchProps('name'),
    },
    {
      title: 'Invoice Code',
      dataIndex: 'invoice_code',
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      filters: [
        { text: 'Set', value: 'set' },
        { text: 'Not Set', value: 'not_set' },
      ],
      onFilter: (value, record) => {
        const val = record.invoice_code;
        return value === 'set' ? !!val : !val;
      },
      render: (val) => val || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'GSTIN',
      dataIndex: 'gstin',
      align: 'center',
      filters: [
        { text: 'Set', value: 'set' },
        { text: 'Not Set', value: 'not_set' },
      ],
      onFilter: (value, record) => {
        const val = record.gstin;
        return value === 'set' ? !!val : !val;
      },
      render: (val) => val || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Operating Model',
      dataIndex: 'operating_model',
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      ...getColumnFilters('operating_model', uniqueValues('operating_model')),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      align: 'center',
      filters: [
        { text: 'Set', value: 'set' },
        { text: 'Not Set', value: 'not_set' },
      ],
      onFilter: (value, record) => {
        const val = record.address;
        return value === 'set' ? !!val : !val;
      },
      render: (val) => val || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      align: 'center',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'error'}>{isActive ? 'ACTIVE' : 'INACTIVE'}</Tag>
      ),
    },
    {
      title: '',
      key: 'edit',
      width: 60,
      render: (_, record) => (
        <EditOutlined className="gst-summary-link" onClick={() => openEditModal(record)} />
      ),
    },
  ];

  const renderRightSideActions = () => (
    <>
      <Select
        showSearch
        placeholder="Select City"
        className="gst-summary-select"
        value={selectedCityId}
        onChange={(value) => chooseCity(value)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        {cities.map((city) => (
          <Option key={city.id} value={city.id}>
            {city.name}
          </Option>
        ))}
      </Select>
    </>
  );

  const totalVenues = venueData.length;
  const activeVenues = venueData.filter((v) => v.is_active).length;
  const missingInvoiceCode = venueData.filter((v) => !v.invoice_code && v.is_active).length;

  const statsConfig = [
    {
      key: 's_total',
      title: 'Total Venues',
      icon: <GlobalOutlined className="gst-summary-icon-blue" />,
    },
    {
      key: 's_active',
      title: 'Active Venues',
      icon: <CheckCircleOutlined className="gst-summary-icon-green" />,
    },
    {
      key: 's_missing_code',
      title: 'Missing Invoice Code',
      icon: <InfoCircleOutlined className="gst-summary-icon-yellow" />,
    },
  ];

  const totals = {
    s_total: totalVenues,
    s_active: activeVenues,
    s_missing_code: missingInvoiceCode,
  };

  return (
    <>
      <SummaryLayout
        title="Venue Configs"
        headerContent={renderRightSideActions()}
        statsConfig={statsConfig}
        totals={totals}
        statsLoading={loading}
        tableProps={{
          rowKey: 'id',
          columns,
          data: venueData,
          loading: loading,
          pagination: { pageSize: 20, showSizeChanger: false },
        }}
      />
      <Modal
        title={`Edit ${selectedVenue?.name}`}
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={saveVenueConfig}
        okText="Submit"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Operating Model" name="operating_model">
            <Select>
              <Select.Option value={10}>Full Ops Center</Select.Option>
              <Select.Option value={20}>Franchise Center</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Invoice Code" name="invoice_code">
            <Input />
          </Form.Item>

          <Form.Item label="GSTIN" name="gstin">
            <Input maxLength={15} />
          </Form.Item>

          <Form.Item label="Address" name="address">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default VenueConfig;
